import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ApplicationHttpClient } from 'app/components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import {
    GablePendingQuestions,
    GableQuestion,
} from './gable-stream.service';
import { PageContextService } from './page-context.service';
import { GableScopeService } from './gable-scope.service';

/**
 * Citation returned by the assistant orchestrator — points at a row in one
 * of the v_* views (or the underlying base table) so the UI can later render
 * a deep link.
 */
export interface GableCitation {
    table: string;
    id: number;
}

/**
 * A pending write proposal awaiting human approval. Created by the AI via
 * `GenericModelCrudTool` in propose mode; committed (or rejected) via the
 * approve/reject endpoints.
 */
/**
 * Hardcoded allowlist of model class names. Mirrors
 * GenericModelCrudTool::ALLOWED_MODELS on the backend. Gable pins this so
 * any drift between FE and BE is a TS compile error; Phase 1 swaps this for
 * a server-driven capability registry where the FE doesn't need a literal
 * union at all.
 */
export type GableModelClass = 'Unit' | 'Vendor' | 'ViolationStatement';

/**
 * The server's per-viewer verdict on a proposed action.
 *
 * `whoCan` names the people who hold the right instead, so the UI can say
 * "Dana Reyes and 2 others must approve" rather than a dead-end refusal.
 */
export interface GableViewerCapability {
    canPerform: boolean;
    reason: string;
    whoCan: string[];
}

/**
 * Turn a failed proposal commit into text worth showing.
 *
 * Core returns business-rule refusals as 403 `not_authorized` / 422 `commit_failed`, both
 * carrying a human-written `reason`. Note this deliberately does NOT go through
 * GrowlerService.fromErrorResponse(): the shared extractor only reads `errors[]` or
 * `message`, so our `reason` field would be dropped and replaced with a generic string —
 * which is the exact bug this ticket exists to fix.
 *
 * Anything else (500, network drop) has no useful server text and gets a generic line, so a
 * refusal ("you can't do this") never looks like a failure ("we couldn't tell").
 */
export function gableCommitFailureMessage(err: unknown): string {
    const status = (err as { status?: number })?.status;
    const reason = (err as { error?: { reason?: string } })?.error?.reason;

    if (status === 429) {
        return 'Too many actions at once. Try again in a moment.';
    }

    if ((status === 403 || status === 422) && typeof reason === 'string' && reason.trim() !== '') {
        return reason;
    }

    return 'That action could not be completed. Please try again.';
}

/** True when the refusal is terminal — retrying can only fail again. */
export function isGableTerminalRefusal(err: unknown): boolean {
    return (err as { error?: { error?: string } })?.error?.error === 'proposal_not_actionable';
}

/**
 * Display context for the record a background suggestion is ABOUT — who, which unit,
 * what they asked for. Lets the queue title cards by the record instead of the
 * suggestion. Null on chat-origin proposals, which have no trigger record.
 */
export interface GableRecordContext {
    title: string | null;
    unitLabel: string | null;
    requesterName: string | null;
    openedAt: string | null;
    /** Amenity bookings only: the amenity whose detail page lists this booking. */
    amenityId?: number | null;
}

export interface GablePendingProposal {
    id: number;
    /** Eloquent short name for legacy CRUD rows, or `action:<type>` for typed actions. */
    modelClass: GableModelClass | string;
    /** Typed-action rows only (e.g. 'approve_payable'); null for legacy CRUD. */
    actionType: string | null;
    operation: 'create' | 'update' | 'delete';
    entityId: number | null;
    /**
     * The record this suggestion is ABOUT — the FormSubmission id for background Requests
     * suggestions, and the only field carrying it (`entityId` is always null on typed actions).
     * Null for chat-origin proposals, which have no trigger record. Used to cluster several
     * suggestions for the same request into one queue card (SD-9015).
     */
    triggerEntityId: number | null;
    /** Suggestion module ('requests'); null for chat-origin proposals. */
    module: string | null;
    /**
     * Can the CURRENT viewer perform this action, and who can if not. Background
     * proposals are staged per-org with no user attached, so the server answers this at
     * read time, when it knows who is asking. Null when the action has no per-user gate.
     */
    viewerCapability: GableViewerCapability | null;
    /** Who/where/what for the trigger record, so cards can be titled by the record. */
    recordContext: GableRecordContext | null;
    payload: Record<string, unknown>;
    /** Persisted section bundle for typed-action rows — renderable via app-gable-action-preview-card. */
    preview: { sections: unknown[]; actions?: unknown; record_link?: { label?: string; route?: string } } | null;
    summary: string;
    createdAt: string | null;
    expiresAt: string;
    /** PMC portfolio rows only (SD-9074 wave 2) — which managed community the row belongs to. */
    organizationId?: number;
    organizationName?: string;
}

/** One decided proposal in the Activity feed (GET proposals/activity). */
export interface GableActivityItem {
    id: number;
    actionType: string | null;
    operation: 'create' | 'update' | 'delete';
    status: 'approved' | 'rejected' | 'expired' | 'superseded' | 'snoozed';
    summary: string;
    createdAt: string | null;
    decidedAt: string | null;
    /** Commit-log result payload for approved rows; null otherwise. */
    result: Record<string, unknown> | null;
    /** Dismiss reason enum for rejected rows (controller-built, camelCase); null otherwise. */
    dismissReason?: string | null;
    /** PMC portfolio rows only (SD-9074 wave 2) — which managed community the row belongs to. */
    organizationId?: number;
    organizationName?: string;
}

export interface GableActivityFeedResponse {
    items: GableActivityItem[];
    page: number;
    perPage: number;
    total: number;
    lastPage: number;
}

/** Per-action-type counts (GET proposals/activity-summary) — feeds the hero's module quick links. */
export interface GableActivitySummaryItem {
    actionType: string;
    pending: number;
    decided: number;
}

/**
 * Tools whose results render as inline tool-cards in the drawer. Single
 * source of truth — consumed by both the `GableToolCard.tool` type
 * union AND the Pusher-fallback heuristic in `GableDrawerService` so the
 * two can't drift.
 */
export const CARD_PRODUCING_TOOLS = ['generate_report', 'redirect_to_budget', 'open_page', 'propose_action', 'build_document', 'export_records', 'analyze_ledger'] as const;

export type CardProducingTool = typeof CARD_PRODUCING_TOOLS[number];

/** Tools whose card is a `{label, url, is_draft}` link rendered by the budget-redirect card (SD-10027). */
export const REDIRECT_CARD_TOOLS: ReadonlyArray<CardProducingTool> = ['redirect_to_budget', 'open_page'];

export function isRedirectCardTool(tool: string): boolean {
    return (REDIRECT_CARD_TOOLS as ReadonlyArray<string>).includes(tool);
}

/**
 * A structured tool-result payload the UI renders inline. Currently
 * only emitted by `generate_report` — its payload carries the full
 * report data + signed download URLs + a deeplink to the dedicated
 * Reports page so the drawer can render the table via `<app-report-table>`
 * with the same fidelity as `/app/reports/*`.
 */
/** The saved assistant message as core has it: cards plus the guarded final text. */
export interface GableSavedMessage {
    toolCards: GableToolCard[];
    content?: string;
    streamState?: string;
}

export interface GableToolCard {
    tool: CardProducingTool | string; // string fallback for future card kinds
    payload:
        | GenerateReportCardPayload
        | BudgetRedirectCardPayload
        | ActionPreviewCardPayload
        | DeliverableCardPayload
        | LedgerIntelligenceCardPayload
        | Record<string, unknown>;
}

/**
 * Section discriminated union for the action-preview card. Backend emits
 * snake_case; backend → UI middleware does NOT touch these JSON blobs
 * (they live inside a tool-card `payload` envelope), so keys here stay
 * snake_case to mirror the wire shape exactly. Section components do their
 * own narrowing on `kind`.
 */
export type ActionPreviewSection =
    | ActionPreviewHeaderSection
    | ActionPreviewStatusPickerSection
    | ActionPreviewEmailTemplateSection
    | ActionPreviewToggleSection
    | ActionPreviewRecipientsSection
    | ActionPreviewRecipientsPeopleSection
    | ActionPreviewImpactSummarySection
    | ActionPreviewKeyValueSection
    | ActionPreviewInfoBlockSection
    | ActionPreviewTableSection
    | ActionPreviewDeepLinkSection
    | ActionPreviewOutcomeSection
    | ActionPreviewWhySection
    | ActionPreviewGuardrailChipsSection
    | ActionPreviewChoiceSection;

export interface ActionPreviewHeaderSection {
    kind: 'header';
    title: string;
    subtitle?: string;
    statusChip?: ActionPreviewChip;
}

export interface ActionPreviewEmailTemplateSection {
    kind: 'email_template';
    label: string;
    subject: string;
    body: string;
    footnote?: { icon?: string; text: string };
    editable: boolean;
}

/**
 * Status dropdown rendered as its own card section so actions other than
 * violations (charges, requests, etc.) can reuse it without burying the
 * value inside an email-template edit modal. Always-editable; the value
 * flows through `pendingEdits.status_picker.value` on the card host and
 * is posted back on commit-action.
 */
export interface ActionPreviewStatusPickerSection {
    kind: 'status_picker';
    label: string;
    value: string;
    options: ActionPreviewStatusOption[];
}

export interface ActionPreviewStatusOption {
    value: string;
    label: string;
    tone?: ActionPreviewChip['tone'];
}

/**
 * Boolean checkbox for optional side effects (e.g. "send email"). The
 * `key` field is the slot under `pendingEdits.toggles[key]` — each action
 * names its own keys, so one section type covers send_email / notify_board
 * / log_to_history without any new component work.
 */
export interface ActionPreviewToggleSection {
    kind: 'toggle';
    key: string;
    label: string;
    description?: string;
    value: boolean;
}

export interface ActionPreviewRecipientsSection {
    kind: 'recipients';
    label: string;
    countSuffix?: string;
    listActions?: { id: string; label: string }[];
    items: ActionPreviewRecipientItem[];
    /** True count of targets when `items` is truncated (SD-9212 bulk actions) — absent/equal to items.length means no truncation. */
    totalCount?: number;
    /** Noun for the scale line ("194 {targetNoun}") — core bakes counts into `countSuffix`, so the scale line reads this instead. Defaults to 'units'. */
    targetNoun?: string;
}

export interface ActionPreviewRecipientItem {
    id: string;
    primary: string;
    secondary?: string;
    removable?: boolean;
    removed?: boolean;
}

/**
 * People-picker variant of the recipients section — renders `app-people-picker`
 * (multi-select + free-text email) instead of the unit selector. Pre-selected
 * `items` use `person-{membershipId}` ids; the edit envelope (shared with the
 * unit variant) carries `person-{id}` and `email:{addr}` ids.
 */
export interface ActionPreviewRecipientsPeopleSection {
    kind: 'recipients_people';
    label: string;
    countSuffix?: string;
    note?: string;
    items: ActionPreviewRecipientItem[];
    /** True count of targets when `items` is truncated (SD-9212 bulk actions) — absent/equal to items.length means no truncation. */
    totalCount?: number;
    /** Noun for the scale line ("194 {targetNoun}") — core bakes counts into `countSuffix`, so the scale line reads this instead. Defaults to 'recipients'. */
    targetNoun?: string;
}

export interface ActionPreviewImpactSummarySection {
    kind: 'impact_summary';
    label: string;
    primary: string;
    secondary?: string;
    tone?: 'info' | 'success' | 'warning';
    /**
     * Token template for recomputing `primary` from the LIVE recipient
     * selection: `{count}`, `{s}` (plural suffix), `{per_unit}`, `{total}`.
     * The summarizer fills it instead of regex-editing the baked string.
     */
    primary_template?: string;
    /** Per-recipient amount in integer cents — feeds `{per_unit}`/`{total}`. */
    per_unit_cents?: number;
    /**
     * camelCase forms the response middleware (CamelCaseResponseKeys) actually
     * delivers for the nested preview payload — read these first, snake as the
     * fixture/back-compat fallback.
     */
    primaryTemplate?: string;
    perUnitCents?: number;
}

export interface ActionPreviewKeyValueSection {
    kind: 'key_value';
    label?: string;
    items: { label: string; value: string }[];
}

export interface ActionPreviewInfoBlockSection {
    kind: 'info_block';
    label?: string;
    body: string;
    tone?: 'info' | 'warning';
}

export interface ActionPreviewTableSection {
    kind: 'table';
    label?: string;
    columns: unknown[];
    rows: unknown[];
}

/**
 * Deep link to the underlying record the action affects (spec §4.1). Renders
 * a single routerLink line ("Open the full request") with a launch icon so the
 * user can jump to the source record before confirming. `route` is an
 * org-relative Angular app path.
 */
export interface ActionPreviewDeepLinkSection {
    kind: 'deep_link';
    label: string;
    route: string;
    record_type: string;
}

/**
 * "If you confirm" outcome section (spec §4.2) — the authorization line that
 * spells out the concrete effect of confirming. Rendered as a blue info panel:
 * `label` overline, `effect` as the emphasized effect line, `record` as the
 * affected-record line, and an optional pre-formatted USD `amount`.
 */
export interface ActionPreviewOutcomeSection {
    kind: 'outcome';
    label: string;
    effect: string;
    record?: string;
    amount?: string;
}

/**
 * Why + confidence section (spec §4.3). `body` is a short plain-prose
 * rationale; `confidence` picks the chip tier (high=green "High confidence",
 * review=blue "Needs your judgment", low=orange "Needs your call"). Omit
 * `confidence` to render no chip.
 */
export interface ActionPreviewWhySection {
    kind: 'why';
    label: string;
    confidence?: 'high' | 'review' | 'low';
    body: string;
}

/**
 * Guardrail chips row (spec §4.4) — a pill row of safety reassurances
 * ("Approver on the roster", "Recognized vendor"), each with a green shield icon.
 */
export interface ActionPreviewGuardrailChipsSection {
    kind: 'guardrail_chips';
    chips: { label: string }[];
}

/**
 * Low-confidence choice section (spec §5) — a select that GATES the primary
 * confirm. When `suggested` is null Gable genuinely can't tell, so nothing is
 * preselected and confirm stays disabled until the user picks; when non-null
 * it is preselected and confirm is enabled. `field_key` is fixed to "choice"
 * (the edits-envelope slot). The card renders a live outcome line from
 * `effect_prefix` + the selected option label. Emits the chosen value which
 * the host merges into `pendingEdits.choice`.
 */
export interface ActionPreviewChoiceSection {
    kind: 'choice';
    label: string;
    field_key: string;
    options: ActionPreviewChoiceOption[];
    suggested: string | null;
    hint?: string;
    effect_prefix?: string;
    /** camelCase form delivered by the response middleware (read first). */
    effectPrefix?: string;
}

export interface ActionPreviewChoiceOption {
    value: string;
    label: string;
}

export interface ActionPreviewChip {
    label: string;
    tone?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'muted';
}

export interface ActionPreviewActions {
    destructive: { id: string; label: string };
    primary: {
        id: string;
        label: string;
        icon?: string;
        /** `{count}`/`{s}` token template for the live-count button label. */
        label_template?: string;
        /** camelCase form delivered by the response middleware (read first). */
        labelTemplate?: string;
    };
}

export interface ActionPreviewCardPayload {
    /** Section-contract version (SD-7967). Absent on pre-versioning payloads — treat as 1. */
    schema_version?: number;
    proposal_id: number;
    action_type: string;
    preview: { sections: ActionPreviewSection[]; actions: ActionPreviewActions };
    state: 'pending' | 'committing' | 'committed' | 'cancelled';
}

export interface ActionPreviewCommitResponse {
    state: 'committed';
    proposalId: number;
    commitLogId: number;
    idempotent: boolean;
    result: Record<string, unknown>;
}

export interface ActionPreviewRefineResponse {
    proposalId: number;
    actionType: string;
    preview: { sections: ActionPreviewSection[]; actions: ActionPreviewActions };
    edits: ActionPreviewEdits;
}

/**
 * In-flight edit delta the UI accumulates on the card. Keyed by section
 * kind so each section emits only the shape it owns. Posted back to the
 * server on `refine` (utility action click) and on `commit-action` (primary
 * confirm). Server re-applies edits to params before validating + executing.
 */
export interface ActionPreviewEdits {
    email_template?: { subject?: string; body?: string };
    recipients?: { removed_ids?: string[]; added_ids?: string[] };
    status_picker?: { value: string };
    toggles?: Record<string, boolean>;
    choice?: { value: string };
}

export interface BudgetRedirectCardPayload {
    label: string;        // e.g. "Go to budgets now"
    url: string;          // /app/budget-drafts or /app/budgets/detail
    is_draft: boolean;    // discriminator: drafts page vs live budget detail
    helper_text?: string; // short context line under the link
}

export interface GenerateReportCardPayload {
    report_key: string;
    report_name: string;
    org_name?: string;
    date_range?: string | null;
    generated_at?: string | null;
    reports_page_url?: string | null;
    downloads: {
        pdf: { url: string; file_name: string; expires_in_hours: number; expires_at?: string | null };
        csv: { url: string; file_name: string; expires_in_hours: number; expires_at?: string | null };
    };
    data: {
        columns: unknown[];
        rows: unknown[];
        summary?: Record<string, unknown>;
        subtitle?: string;
        extra?: Record<string, unknown>;
    };
    digest?: string;
    row_count?: number;
}

/**
 * Runtime narrowing guard for `generate_report` tool-card payloads.
 * Checks the minimal shape the drawer relies on (downloads + tabular data).
 */
export function isGenerateReportCardPayload(p: unknown): p is GenerateReportCardPayload {
    // Payload keys are emitted snake_case by core but the response middleware
    // camelCases them before they reach the UI — so we only assert on the
    // shape the drawer template actually relies on (tabular `data` + the
    // `downloads` object) and let casing differences pass through.
    if (typeof p !== 'object' || p === null) {
        return false;
    }
    const obj = p as Record<string, unknown>;
    const data = obj['data'];
    if (typeof data !== 'object' || data === null) {
        return false;
    }
    const dataObj = data as Record<string, unknown>;
    if (!Array.isArray(dataObj['columns']) || !Array.isArray(dataObj['rows'])) {
        return false;
    }
    const downloads = obj['downloads'];
    if (typeof downloads !== 'object' || downloads === null) {
        return false;
    }
    return true;
}

/**
 * `build_document` deliverable card — a generated document (e.g. board summary
 * .docx/.pdf) with token-authed 24h download links. Keys stay camelCase: tool-card
 * payload envelopes are NOT re-cased by the response middleware.
 */
export interface DeliverableCardFile {
    format: string; // 'docx' | 'pdf'
    fileName: string;
    sizeLabel: string;
    downloadUrl: string;
    previewable: boolean; // reserved for a future in-drawer Preview button (SD-7242); not rendered yet
}

export interface DeliverableCardPayload {
    title: string;
    files: DeliverableCardFile[];
    savedTo?: string;
    // Payload-only for now: per PO direction the card uses a single Download button +
    // format dropdown rather than "tools used" chips. Kept for telemetry / future use.
    toolsUsed?: string[];
}

/** Tools whose tool-card renders through the deliverable card. */
export function isDeliverableCardTool(tool: string): boolean {
    return tool === 'build_document' || tool === 'export_records';
}

/**
 * Runtime narrowing guard for `build_document` tool-card payloads. Requires a
 * non-empty `files` list where each entry has at least a `format` + `downloadUrl`
 * — otherwise the card could render an `<a href="undefined">`. A malformed/empty
 * payload is skipped via the warn-and-return-null path in `asDeliverableCardPayload`.
 */
export function isDeliverableCardPayload(p: unknown): p is DeliverableCardPayload {
    if (typeof p !== 'object' || p === null) {
        return false;
    }
    const obj = p as Record<string, unknown>;
    if (typeof obj['title'] !== 'string' || !Array.isArray(obj['files']) || obj['files'].length === 0) {
        return false;
    }
    return (obj['files'] as unknown[]).every((file) => {
        if (typeof file !== 'object' || file === null) {
            return false;
        }
        const f = file as Record<string, unknown>;
        return typeof f['format'] === 'string' && typeof f['downloadUrl'] === 'string';
    });
}

/**
 * `analyze_ledger` deterministic tool-card — the AUTHORITATIVE numeric
 * display for a Ledger Intelligence answer; the model's prose is
 * commentary only. Keys stay camelCase: `result` is the response
 * middleware's camelCased view of core's `AnalyzeLedgerResult` DTO, and
 * the outer wrapper (`schemaVersion` + `result`) is NOT re-cased further
 * (tool-card payload envelopes are opaque to CamelCaseResponseKeys).
 */
export interface LedgerIntelligencePeriod {
    kind: string;
    from: string | null;
    to: string | null;
    label: string;
}

/** A single top-line metric. Money and count highlights both just render `formattedValue`. */
export interface LedgerIntelligenceHighlight {
    key: string;
    label: string;
    valueType: 'money' | 'count';
    valueCents?: number;
    value?: number;
    formattedValue: string;
}

export interface LedgerIntelligenceTableColumn {
    key: string;
    label: string;
    type: 'text' | 'money' | 'count';
}

/** A money cell inside a table row — text/count cells are a raw string|number instead. */
export interface LedgerIntelligenceMoneyCell {
    valueCents: number;
    formattedValue: string;
}

export type LedgerIntelligenceCellValue = LedgerIntelligenceMoneyCell | string | number | null;

export interface LedgerIntelligenceTable {
    key: string;
    title: string;
    columns: LedgerIntelligenceTableColumn[];
    rows: Record<string, LedgerIntelligenceCellValue>[];
    truncated: boolean;
    returnedCount: number;
    totalCount?: number;
    /** Tokenised CSV of the rows the card couldn't show; only set when truncated. */
    csvUrl?: string;
    csvRowCount?: number;
}

export interface LedgerIntelligenceCheck {
    key: string;
    label: string;
    status: 'passed' | 'failed' | 'not_run';
    differenceCents?: number;
    formattedDifference?: string;
    detail?: string;
}

export interface LedgerIntelligenceWarning {
    key: string;
    message: string;
}

export interface LedgerIntelligenceProvenance {
    complete: boolean;
    asOf: string | null;
    calculationVersion: string;
    sources: string[];
    filters: Record<string, unknown>;
}

/** The `result` object — this is what the card component actually binds to. */
export interface LedgerIntelligenceResult {
    schemaVersion: string;
    status: 'answered' | string;
    analysisType: string;
    title: string;
    subtitle?: string;
    period: LedgerIntelligencePeriod;
    accountingMode: string;
    basis: string;
    highlights: LedgerIntelligenceHighlight[];
    tables: LedgerIntelligenceTable[];
    checks: LedgerIntelligenceCheck[];
    warnings: LedgerIntelligenceWarning[];
    provenance: LedgerIntelligenceProvenance;
    durationMs?: number;
}

/** The tool-card payload envelope as it lands in `GableToolCard.payload`. */
export interface LedgerIntelligenceCardPayload {
    // Integer over the wire (core emits schema_version: 1, camelCased by the
    // response middleware). Carried for completeness; the UI only reads `result`.
    schemaVersion: number;
    result: LedgerIntelligenceResult;
}

/**
 * Runtime narrowing guard for `analyze_ledger` tool-card payloads. Requires
 * the `result` envelope plus the minimal shape the card relies on
 * (title + array fields) — a malformed payload is skipped via the
 * warn-and-return-null path in `asLedgerIntelligencePayload`.
 */
export function isLedgerIntelligenceCardPayload(p: unknown): p is LedgerIntelligenceCardPayload {
    if (typeof p !== 'object' || p === null) {
        return false;
    }
    const obj = p as Record<string, unknown>;
    const result = obj['result'];
    if (typeof result !== 'object' || result === null) {
        return false;
    }
    const resultObj = result as Record<string, unknown>;
    if (typeof resultObj['title'] !== 'string') {
        return false;
    }
    return (
        Array.isArray(resultObj['highlights']) &&
        Array.isArray(resultObj['tables']) &&
        Array.isArray(resultObj['checks']) &&
        Array.isArray(resultObj['warnings'])
    );
}

/**
 * Runtime narrowing guard for `redirect_to_budget` tool-card payloads.
 */
export function isActionPreviewCardPayload(p: unknown): p is ActionPreviewCardPayload {
    if (typeof p !== 'object' || p === null) {
        return false;
    }
    const obj = p as Record<string, unknown>;
    const preview = obj['preview'];
    if (typeof preview !== 'object' || preview === null) {
        return false;
    }
    const previewObj = preview as Record<string, unknown>;
    if (!Array.isArray(previewObj['sections'])) {
        return false;
    }
    const actions = previewObj['actions'];
    if (typeof actions !== 'object' || actions === null) {
        return false;
    }
    return typeof (obj['proposal_id'] ?? obj['proposalId']) === 'number';
}

export function isBudgetRedirectCardPayload(p: unknown): p is BudgetRedirectCardPayload {
    if (typeof p !== 'object' || p === null) {
        return false;
    }
    // Accept either snake_case (raw core payload) or camelCase (post-middleware)
    // for the `is_draft` / `isDraft` discriminator.
    const obj = p as Record<string, unknown>;
    const isDraft = obj['is_draft'] ?? obj['isDraft'];
    return (
        typeof obj['label'] === 'string' &&
        typeof obj['url'] === 'string' &&
        typeof isDraft === 'boolean'
    );
}

/** A file sent with a chat turn: a fresh upload, or a document already in the association's library. */
export interface GableAttachmentRef {
    uploadedFileId?: number;
    documentId?: number;
}

/**
 * Feedback values matching the backend `FeedbackState` enum.
 *  - `'good'` / `'bad'` are the two thumbs states
 *  - `'none'` clears feedback (toggled when re-clicking an active thumb)
 */
export type GableFeedback = 'good' | 'bad' | 'none';

/**
 * Reason-chip selections users can attach to a thumbs-down feedback.
 * Telemetry-only.
 */
export type GableFeedbackChip =
    | 'wrong_data'
    | 'missed_nuance'
    | 'wrong_tone'
    | 'off_topic'
    | 'other';

/**
 * Per-question answer payload the UI sends back. Value type depends on
 * the question's `kind`:
 *   - `single_select` → string (chosen option value, OR free-form text
 *     when the user picked "Something else" on a question with
 *     `allow_other: true`)
 *   - `multi_select`  → string[] (option values, possibly mixed with
 *     free-form text when the user added "Something else")
 *   - `text`          → string
 *   - `number`        → number
 *   - `date`          → string (ISO `yyyy-mm-dd`)
 */
export interface GableQuestionAnswer {
    id: string;
    value: string | string[] | number;
}

export interface GableAnswerQuestionsBody {
    conversationId: number;
    batchId: string;
    answers: GableQuestionAnswer[];
    /**
     * Echo the original questions array back. The backend uses this to
     * map raw option `value`s ("1234") back to their human-readable
     * `label`s ("Lot 4B") when synthesizing the user message the AI
     * sees. Strongly recommended per backend contract — without this
     * the AI sees only opaque ids.
     */
    questionsEcho?: GableQuestion[];
}

/**
 * Handshake response from the chat-stream endpoint. Tells the UI which Echo
 * channel to subscribe to (`private-ai-conversation-{conversationId}`) and which
 * assistant message id is about to start receiving streamed deltas.
 */
export interface GableChatStreamResponse {
    conversationId: number;
    assistantMessageId: number;
    /** Present when the title was already generated by the time the turn was accepted. */
    title?: string | null;
    /**
     * Present when core could not forward the turn at all (box down —
     * `error: 'box_unavailable'`, SD-10526). Still a 202: the conversation and
     * both rows are real, but the turn already ended — do not open the Echo
     * channel for it. `message` is the user-facing copy.
     */
    streamState?: 'failed';
    error?: string;
    message?: string;
}

export interface GablePendingListResponse {
    proposals: GablePendingProposal[];
}

/** One page starter chip. */
export interface GableWelcomeSuggestion {
    label: string;
    prompt: string;
}

/**
 * A group of drawer empty-state suggestions. `kind` is open-ended on
 * purpose: today only 'page_starter', but Gable Autopilot will add proactive
 * groups (review-queue nudges, etc.) here without a breaking change.
 */
export interface GableWelcomeGroup {
    kind: string;
    label: string;
    suggestions: GableWelcomeSuggestion[];
}

export interface GableWelcomeSuggestionsResponse {
    groups: GableWelcomeGroup[];
}

/** One chip on a module page's Gable callout (SD-9963); a signal's `label` already carries its count. */
export interface GablePageStarter {
    label: string;
    prompt: string;
    count: number | null;
    kind: 'signal' | 'curated';
}

export interface GablePageStartersResponse {
    page: string;
    computedAt: string;
    starters: GablePageStarter[];
}

// -----------------------------------------------------------------------------
// SD-6742 — chat history
// -----------------------------------------------------------------------------

export type GableConversationStatus = 'active' | 'archived';

/**
 * One row in the conversation history list (GET /conversations). `title` may
 * be null until the server-side title-generation job names it — the UI falls
 * back to the first user message / "New chat".
 */
export interface GableConversationSummary {
    id: number;
    title: string | null;
    status: GableConversationStatus;
    /**
     * Null until the conversation has a message: rows are created before the
     * first turn (`GableOrchestrator`), and the column is nullable
     * (`GableConversationController::summaryShape` emits
     * `last_message_at?->toIso8601String()`).
     */
    lastMessageAt: string | null;
    messageCount: number;
    /** Tenant this thread continues in — present on cross-scope listings. */
    scope?: GableConversationScope;
}

export interface GableConversationScope {
    kind: 'org' | 'pmc';
    id: number | null;
    name: string | null;
}

/**
 * Pagination envelope for the conversation list (SD-6742 server-side
 * search + paging). `hasMore` is the server's authoritative "another page
 * exists" flag — the UI appends pages until it's false rather than
 * inferring from `total`.
 */
export interface GableConversationListPagination {
    page: number;
    perPage: number;
    total: number;
    hasMore: boolean;
}

export interface GableConversationListResponse {
    conversations: GableConversationSummary[];
    pagination: GableConversationListPagination;
    /**
     * The tenants this user's conversations live in — the archived panel's scope
     * filter menu. Server-owned because the filter itself is server-side: once a
     * scope is applied every returned row shares it, so the menu can no longer be
     * derived from the rows. Present only on an `allScopes` listing.
     */
    scopes?: GableConversationScope[];
}

/** Server-side ordering for the conversation list. */
export type GableConversationSort = 'recent' | 'oldest' | 'title';

/**
 * A tenant the conversation list can be narrowed to. Narrower than
 * {@link GableConversationScope}: an option the user can actually pick always
 * has an id and needs no display name.
 */
export interface GableConversationScopeRef {
    kind: 'org' | 'pmc';
    id: number;
}

/** Params for the conversation list endpoint (server-side search + paging). */
export interface GableListConversationsParams {
    status?: GableConversationStatus;
    /** Trimmed search term; matches title OR message content server-side. */
    search?: string;
    /** 1-based page number (default 1 server-side). */
    page?: number;
    /** Page size (default 25, max 50 server-side). */
    perPage?: number;
    /** List every conversation this user owns, in any org/PMC, not just the current scope. */
    allScopes?: boolean;
    /**
     * Ordering (default 'recent'). Server-side because the list is paged —
     * sorting the fetched pages only orders what is already on screen, so
     * "oldest first" could never reach the oldest row.
     */
    sort?: GableConversationSort;
    /**
     * Narrow to one tenant. Server-side for the same reason as `sort`, plus one
     * of its own: a client-side filter over a paged list can leave the panel too
     * short to scroll, and scrolling is what fetches the next page.
     */
    scope?: GableConversationScopeRef;
}

/** Conversation envelope returned by load / rename / archive endpoints. */
export interface GableConversationDetail {
    id: number;
    title: string | null;
    status: GableConversationStatus;
    /** Null until the conversation has a message — see {@link GableConversationSummary}. */
    lastMessageAt: string | null;
}

/**
 * One persisted message returned when loading a conversation for resume.
 * Assistant rows carry best-effort reconstructed metadata; user rows carry
 * just role/content/timestamp. Keys are camelCase (response middleware
 * re-cases the top-level envelope; the nested arrays mirror the same shapes
 * the live stream produces).
 */
export interface GableConversationMessage {
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
    messageId: number;
    /** Per-conversation order assigned by the server (SD-10033). */
    sequence?: number;
    interactionId?: number | null;
    streamState?: 'streaming' | 'complete' | 'failed' | 'cancelled';
    /**
     * Present only on a CMA long-deliverable card row (option B). Signals the
     * drawer to rehydrate this row into the Activity-tab task list instead of
     * rendering it as an inline chat card. Keyed by runId so it dedupes with a
     * live `ai.sandbox.ready` task from the same run.
     */
    sandboxRunId?: string | null;
    citations?: GableCitation[];
    toolCards?: GableToolCard[];
    thoughts?: { id?: string; label: string; ok?: boolean; kind?: 'tool' | 'summary' | 'narration' }[];
    durationMs?: number;
    followUpSuggestions?: string[];
}

export interface GableLoadConversationResponse {
    conversation: GableConversationDetail;
    messages: GableConversationMessage[];
}

export interface GableConversationMutationResponse {
    conversation: GableConversationDetail;
}

export interface GableApproveResponse {
    committed: true;
    result: Record<string, unknown>;
    commitLogId: number;
    idempotent?: true;
}

export interface GableRejectResponse {
    rejected: true;
}

export interface GableSnoozeResponse {
    snoozed: true;
    snoozedUntil: string;
}

/** Dismiss-reason enum accepted by {@link GableService.rejectProposal}. */
export type GableDismissReason = 'not_needed' | 'manual' | 'wrong' | 'policy';

/**
 * HTTP client for Gable, PayHOA's AI assistant.
 *
 * Wraps the four endpoints the Gable page uses:
 *  - POST /gable/assistant/organizations/{orgId}/chat
 *  - GET  /gable/assistant/organizations/{orgId}/proposals/pending
 *  - POST /gable/assistant/organizations/{orgId}/proposals/{id}/approve
 *  - POST /gable/assistant/organizations/{orgId}/proposals/{id}/reject
 *
 * Thin transport — the page owns conversation/proposal state.
 */
@Injectable({
    providedIn: 'root',
})
export class GableService {
    constructor(
        private _http: ApplicationHttpClient,
        private _pageContext: PageContextService,
        private _scope: GableScopeService,
    ) {}

    /** Org id for org-family routes: the drill-down community in pmc-drill scope (SD-9209), else the JWT org. */
    private _effectiveOrgId(jwt: JwtLegFiClaims): number {
        return this._scope.drillDownOrgId ?? jwt.orgId;
    }

    /**
     * Resolve the tenant ONE conversation's request targets. A row listed with
     * `allScopes` carries its own scope and has to be acted on THERE — the
     * drawer's current scope is only the fallback for rows without one.
     * Otherwise a portfolio-scoped drawer PATCHes an org row under /pmcs/{id},
     * where the server can't see it, and every write 404s (SD-6742 follow-up).
     */
    private _conversationScopeIds(
        jwt: JwtLegFiClaims,
        scope?: GableConversationScope,
    ): { pmcId: number | null; orgId: number } {
        return {
            pmcId: scope?.id != null
                ? (scope.kind === 'pmc' ? scope.id : null)
                : this._scope.portfolioPmcId,
            orgId: scope?.kind === 'org' && scope.id != null
                ? scope.id
                : this._effectiveOrgId(jwt),
        };
    }

    /**
     * Kick off a streaming assistant turn. Returns a handshake (conversation
     * id + assistant message id) immediately — the actual response text is
     * delivered as `ai.stream.*` events on the
     * `private-ai-conversation-{conversationId}` Echo channel (lowercased,
     * hyphenated — uppercase/dotted names have bitten us before).
     *
     * Callers should subscribe to that channel as soon as this resolves and
     * append text-delta payloads to the placeholder bubble keyed on
     * `assistantMessageId`. The response is a 202 even when the turn already
     * ended without ever streaming (`streamState: 'failed'` — box down, SD-10526);
     * callers must check that before opening the Echo channel. There is no
     * blocking fallback any more: POST /chat is gone from core.
     */
    public chatStream(
        message: string,
        conversationId: number | null,
        attachments?: GableAttachmentRef[],
        scope?: GableConversationScope,
        clientTurnId?: string,
    ): Observable<GableChatStreamResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableChatStreamResponse>;
        }

        // Continuing a chat opened from another tenant stays in THAT tenant.
        const { pmcId, orgId } = this._conversationScopeIds(jwt, scope);
        const url = Routes.MakeLegFiCoreUrl(
            pmcId !== null
                ? Routes.GableModule.ChatStreamPmc(pmcId)
                : Routes.GableModule.ChatStream(orgId),
        );

        const body: Record<string, unknown> = { message };
        if (conversationId !== null && conversationId !== undefined) {
            body['conversationId'] = conversationId;
        }
        if (attachments && attachments.length > 0) {
            body['attachments'] = attachments;
        }
        const pageContext = this._pageContext.snapshot();
        if (pageContext) {
            body['pageContext'] = pageContext;
        }
        // Identifies the attempt, so a retried request returns the turn core already
        // has instead of creating a second user row and stub (SD-10033).
        if (clientTurnId) {
            body['clientTurnId'] = clientTurnId;
        }

        return this._http.post<GableChatStreamResponse>(url, this._http.serializeObject(body));
    }

    /**
     * Page-aware starter suggestions for the drawer empty state. Keyed to the
     * current route (Haiku on the server, cached). Returns a flexible group
     * envelope — today just page starters, later also Autopilot proactive
     * items — so the drawer can render new group kinds without a change here.
     * Best-effort: callers should treat any error as "no groups".
     */
    /**
     * A page callout chip was taken (SD-9964). Fire-and-forget: the drawer already opened
     * with the prompt, so a failed beacon must never surface to the customer.
     */
    public recordCalloutClick(click: { module: string; label: string; prompt: string; url: string }): void {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return;
        }
        const url = Routes.MakeLegFiCoreUrl(Routes.GableModule.CalloutClick(this._effectiveOrgId(jwt)));
        this._http
            .post<void>(url, this._http.serializeObject(click))
            .pipe(catchError(() => of(undefined)))
            .subscribe();
    }

    /** Org-specific chips for a page callout (SD-9963). Errors propagate so the callout can fall back to its own starters. */
    public fetchPageStarters(page: string): Observable<GablePageStartersResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return of({ page, computedAt: '', starters: [] });
        }
        const url = Routes.MakeLegFiCoreUrl(Routes.GableModule.PageStarters(this._effectiveOrgId(jwt)));
        return this._http.get<GablePageStartersResponse>(url, { params: new HttpParams().set('page', page) });
    }

    public welcomeSuggestions(): Observable<GableWelcomeSuggestionsResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return of({ groups: [] });
        }
        const pageContext = this._pageContext.snapshot();
        if (!pageContext?.url) {
            return of({ groups: [] });
        }
        const pmcId = this._scope.portfolioPmcId;
        const url = Routes.MakeLegFiCoreUrl(
            pmcId !== null
                ? Routes.GableModule.WelcomeSuggestionsPmc(pmcId)
                : Routes.GableModule.WelcomeSuggestions(this._effectiveOrgId(jwt)),
        );
        let params = new HttpParams().set('url', pageContext.url);
        if (pageContext.title) {
            params = params.set('title', pageContext.title);
        }
        if (pageContext.componentName) {
            params = params.set('componentName', pageContext.componentName);
        }
        return this._http
            .get<GableWelcomeSuggestionsResponse>(url, { params })
            .pipe(catchError(() => of({ groups: [] })));
    }

    /**
     * Fetch all proposals still pending for this user/org. Used to backfill
     * the approval panel on first page load (before any chat round-trip).
     */
    public listPendingProposals(): Observable<GablePendingListResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GablePendingListResponse>;
        }

        // Proposal surfaces are portfolio-wide in ANY pmc scope (drill included) — raw pmcId, not portfolioPmcId.
        const pmcId = this._scope.pmcId;
        const url = Routes.MakeLegFiCoreUrl(
            pmcId !== null
                ? Routes.GableModule.PendingProposalsPmc(pmcId)
                : Routes.GableModule.PendingProposals(jwt.orgId),
        );

        return this._http.get<GablePendingListResponse>(url);
    }

    /**
     * Decided-proposal history (approved / rejected / expired) for the
     * Activity tab feed. `actionTypes` narrows to a module's action set.
     */
    public getProposalActivity(
            actionTypes?: string[],
            page: number = 1,
            perPage: number = 20,
    ): Observable<GableActivityFeedResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableActivityFeedResponse>;
        }

        const pmcId = this._scope.pmcId;
        const url = Routes.MakeLegFiCoreUrl(
            pmcId !== null
                ? Routes.GableModule.ProposalActivityPmc(pmcId)
                : Routes.GableModule.ProposalActivity(jwt.orgId),
        );
        let params = new HttpParams()
                .set('page', String(page))
                .set('perPage', String(perPage));
        if (actionTypes?.length) {
            params = params.set('actionTypes', actionTypes.join(','));
        }

        return this._http.get<GableActivityFeedResponse>(url, { params });
    }

    /** Per-action-type proposal counts for the hero's module quick links. */
    public getActivitySummary(): Observable<{ items: GableActivitySummaryItem[] }> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<{ items: GableActivitySummaryItem[] }>;
        }

        const pmcId = this._scope.pmcId;
        const url = Routes.MakeLegFiCoreUrl(
            pmcId !== null
                ? Routes.GableModule.ProposalActivitySummaryPmc(pmcId)
                : Routes.GableModule.ProposalActivitySummary(jwt.orgId),
        );

        return this._http.get<{ items: GableActivitySummaryItem[] }>(url);
    }

    // -------------------------------------------------------------------------
    // SD-6742 — chat history (list / load / rename / archive)
    // -------------------------------------------------------------------------

    /**
     * List the current user's conversations for this org, ordered by
     * last_message_at DESC. `status` filters active vs archived (defaults to
     * active server-side; we pass it explicitly so the two lists are
     * unambiguous). `search` runs a server-side match over BOTH the title and
     * message content; `page`/`perPage` drive pagination — the response
     * carries a `pagination` envelope (`hasMore`) so callers can append pages.
     */
    public listConversations(
        params: GableListConversationsParams = {},
    ): Observable<GableConversationListResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableConversationListResponse>;
        }

        const query: string[] = [`status=${params.status ?? 'active'}`];
        const search = (params.search ?? '').trim();
        if (search !== '') {
            query.push(`search=${encodeURIComponent(search)}`);
        }
        if (params.page !== undefined) {
            query.push(`page=${params.page}`);
        }
        if (params.perPage !== undefined) {
            query.push(`perPage=${params.perPage}`);
        }
        if (params.allScopes) {
            query.push('allScopes=1');
        }
        if (params.sort !== undefined && params.sort !== 'recent') {
            query.push(`sort=${params.sort}`);
        }
        if (params.scope !== undefined) {
            // The pair travels together — the server 422s on half of it.
            query.push(`scopeKind=${params.scope.kind}`, `scopeId=${params.scope.id}`);
        }

        const pmcId = this._scope.portfolioPmcId;
        const base = pmcId !== null
            ? Routes.GableModule.ConversationsPmc(pmcId)
            : Routes.GableModule.Conversations(this._effectiveOrgId(jwt));
        const url = Routes.MakeLegFiCoreUrl(`${base}?${query.join('&')}`);

        return this._http.get<GableConversationListResponse>(url);
    }

    /**
     * Load one conversation (header + full message history) for display +
     * resume. The returned `conversationId` is what callers pass to
     * `chatStream` to continue the thread.
     */
    public loadConversation(
        conversationId: number,
        scope?: GableConversationScope,
        afterSequence?: number,
    ): Observable<GableLoadConversationResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableLoadConversationResponse>;
        }

        // A row from the cross-scope list opens in ITS tenant, not the page's.
        const { pmcId, orgId } = this._conversationScopeIds(jwt, scope);
        const url = Routes.MakeLegFiCoreUrl(
            pmcId !== null
                ? Routes.GableModule.ConversationPmc(pmcId, conversationId)
                : Routes.GableModule.Conversation(orgId, conversationId),
        );

        // Recovering from a gap asks for what follows the last sequence we hold, rather
        // than refetching the whole transcript (SD-10033).
        const query = afterSequence !== undefined && afterSequence > 0 ? `?afterSequence=${afterSequence}` : '';

        return this._http.get<GableLoadConversationResponse>(url + query);
    }

    /**
     * Rename a conversation (title <= 255 chars). `scope` is the ROW'S tenant —
     * a cross-scope row renames where it lives, not where the drawer is.
     */
    public renameConversation(
        conversationId: number,
        title: string,
        scope?: GableConversationScope,
    ): Observable<GableConversationMutationResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableConversationMutationResponse>;
        }

        const { pmcId, orgId } = this._conversationScopeIds(jwt, scope);
        const url = Routes.MakeLegFiCoreUrl(
            pmcId !== null
                ? Routes.GableModule.ConversationPmc(pmcId, conversationId)
                : Routes.GableModule.Conversation(orgId, conversationId),
        );

        return this._http.patch<GableConversationMutationResponse>(
            url,
            this._http.serializeObject({ title }),
        );
    }

    /** Archive a conversation (active -> archived) in the ROW'S tenant. */
    public archiveConversation(
        conversationId: number,
        scope?: GableConversationScope,
    ): Observable<GableConversationMutationResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableConversationMutationResponse>;
        }

        const { pmcId, orgId } = this._conversationScopeIds(jwt, scope);
        const url = Routes.MakeLegFiCoreUrl(
            pmcId !== null
                ? Routes.GableModule.ArchiveConversationPmc(pmcId, conversationId)
                : Routes.GableModule.ArchiveConversation(orgId, conversationId),
        );

        return this._http.post<GableConversationMutationResponse>(url, this._http.serializeObject({}));
    }

    /** Unarchive a conversation (archived -> active) in the ROW'S tenant. */
    public unarchiveConversation(
        conversationId: number,
        scope?: GableConversationScope,
    ): Observable<GableConversationMutationResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableConversationMutationResponse>;
        }

        const { pmcId, orgId } = this._conversationScopeIds(jwt, scope);
        const url = Routes.MakeLegFiCoreUrl(
            pmcId !== null
                ? Routes.GableModule.UnarchiveConversationPmc(pmcId, conversationId)
                : Routes.GableModule.UnarchiveConversation(orgId, conversationId),
        );

        return this._http.post<GableConversationMutationResponse>(url, this._http.serializeObject({}));
    }

    /**
     * Commit a pending proposal. The caller generates an idempotency key
     * (UUID v4) so a double-click or retry replays the prior result instead
     * of double-writing.
     */
    public approveProposal(
        proposalId: number,
        idempotencyKey: string,
        // PMC scope passes the ROW'S org (SD-9074 wave 2) — core authorizes PMC staff on managed orgs.
        organizationId?: number,
    ): Observable<GableApproveResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableApproveResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.ApproveProposal(organizationId ?? jwt.orgId, proposalId),
        );

        return this._http.post<GableApproveResponse>(
            url,
            this._http.serializeObject({ idempotencyKey }),
        );
    }

    /**
     * Submit answers for an open question batch. Server response is the same
     * streaming handshake `chatStream()` returns — the answer resumes the turn
     * on the box, and the reply streams over Echo like any other turn.
     */
    public answerQuestions(
        body: GableAnswerQuestionsBody,
    ): Observable<GableChatStreamResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableChatStreamResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.AnswerQuestions(this._effectiveOrgId(jwt)),
        );

        return this._http.post<GableChatStreamResponse>(url, this._http.serializeObject(body));
    }

    /**
     * Record a thumbs-up / thumbs-down on an assistant turn. The
     * `aiInteractionEventId` lives on the chat response (and on the
     * `ai.stream.completed` event for streaming). Backend updates the
     * `feedback` + `feedback_at` + `feedback_user_id` columns on the
     * matching `AiInteractionEvent` row.
     */
    public recordFeedback(
        eventId: number,
        feedback: GableFeedback,
    ): Observable<unknown> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<unknown>;
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.AiTelemetryModule.RecordFeedback(eventId));

        return this._http.patch<unknown>(url, this._http.serializeObject({ feedback }));
    }

    /**
     * Record a reason-chip selection (plus optional free text) on an
     * assistant turn. Telemetry-only. Powers the inline feedback panel
     * under each bubble.
     */
    public recordChipFeedback(
        eventId: number,
        chip: GableFeedbackChip,
        detail?: string,
    ): Observable<unknown> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<unknown>;
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.AiTelemetryModule.RecordChipFeedback(eventId));
        const body: { chip: string; detail?: string } = { chip };
        if (detail) { body.detail = detail; }

        return this._http.patch<unknown>(url, this._http.serializeObject(body));
    }

    /**
     * Mark a pending proposal as rejected. No payload required.
     */
    /**
     * Fetch the persisted toolCards for one assistant message — UI fallback
     * for when Pusher dropped them from the stream-completed broadcast
     * (full report payloads routinely exceed Pusher's ~10KB event limit).
     */
    public fetchToolCards(
        conversationId: number,
        messageId: number,
    ): Observable<GableSavedMessage> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableSavedMessage>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.ToolCards(this._effectiveOrgId(jwt), conversationId, messageId),
        );

        return this._http.get<GableSavedMessage>(url);
    }

    /**
     * Confirm + execute an action-preview proposal. The framework's
     * twin of `approveProposal()` — distinct path because action-preview
     * proposals route through {@see \App\Modules\AIGable\App\Actions\ActionDefinition}
     * rather than the generic Eloquent CRUD allowlist.
     */
    public commitActionProposal(
        proposalId: number,
        idempotencyKey: string,
        edits: ActionPreviewEdits,
        // Optional chat context — when present, core runs the post-commit
        // follow-ups job so "what next" chips appear under the card's message.
        chatContext?: { conversationId: number; messageId: number },
        organizationId?: number,
    ): Observable<ActionPreviewCommitResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<ActionPreviewCommitResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.CommitAction(organizationId ?? jwt.orgId, proposalId),
        );

        return this._http.post<ActionPreviewCommitResponse>(
            url,
            this._http.serializeObject({
                idempotencyKey,
                edits,
                ...(chatContext ? { conversationId: chatContext.conversationId, messageId: chatContext.messageId } : {}),
            }),
        );
    }

    /**
     * Refine an in-flight action-preview proposal — utility-action
     * clicks ("Soften tone", "Restore skipped") and recipient/text
     * edits land here. Server reruns ActionDefinition::refine() and
     * returns the new section bundle the card swaps in.
     */
    public refineActionProposal(
        proposalId: number,
        utilityActionId: string,
        edits: ActionPreviewEdits,
        organizationId?: number,
    ): Observable<ActionPreviewRefineResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<ActionPreviewRefineResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.RefineProposal(organizationId ?? jwt.orgId, proposalId),
        );

        return this._http.post<ActionPreviewRefineResponse>(
            url,
            this._http.serializeObject({ utilityActionId, edits }),
        );
    }

    /**
     * Cancel an in-flight streaming assistant turn (Claude-desktop-style
     * Stop button). Server marks the message as cancelled + writes a
     * cache flag the streaming job polls; on the next Prism callback the
     * job short-circuits and stops emitting deltas. The UI calls this
     * eagerly when the user clicks Stop; whatever has streamed so far
     * stays in the bubble.
     */
    public cancelStream(messageId: number): Observable<{ cancelled: true }> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<{ cancelled: true }>;
        }

        const pmcId = this._scope.portfolioPmcId;
        const url = Routes.MakeLegFiCoreUrl(
            pmcId !== null
                ? Routes.GableModule.CancelStreamPmc(pmcId, messageId)
                : Routes.GableModule.CancelStream(this._effectiveOrgId(jwt), messageId),
        );

        return this._http.post<{ cancelled: true }>(url, this._http.serializeObject({}));
    }

    /**
     * Dismiss (reject) a pending proposal. Optionally records a
     * dismiss-reason enum ({@link GableDismissReason}) so the Activity feed
     * can show WHY it was dismissed. A missing/invalid reason still rejects
     * (backward compatible) with `dismiss_reason` stored null server-side.
     */
    public rejectProposal(
        proposalId: number,
        reason?: GableDismissReason,
        organizationId?: number,
    ): Observable<GableRejectResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableRejectResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.RejectProposal(organizationId ?? jwt.orgId, proposalId),
        );

        return this._http.post<GableRejectResponse>(
            url,
            this._http.serializeObject(reason ? { reason } : {}),
        );
    }

    /**
     * Snooze a pending proposal until the next morning (server computes the
     * exact wake time in the org timezone). The item silently drops out of
     * the pending queue and returns lazily once `snoozed_until` lapses.
     * Guards mirror reject (org+user scoped, pending only).
     */
    public snoozeProposal(proposalId: number, organizationId?: number): Observable<GableSnoozeResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableSnoozeResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.SnoozeProposal(organizationId ?? jwt.orgId, proposalId),
        );

        return this._http.post<GableSnoozeResponse>(url, this._http.serializeObject({}));
    }
}
